import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { GameCard, GameCardSkeleton } from '../components/GameCard';
import HeroVisual from '../components/HeroVisual';
import { useStore } from '../context/StoreContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Zap, Shield, Tag, Headphones, Activity, Cpu, Radio } from 'lucide-react';
import theme from '../theme.config';
const HERO_BG = theme.assets.heroBg;
const COPY = theme.copy;

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'all', name: 'Semua' },
  { id: 'games', name: 'Games' },
  { id: 'pulsa', name: 'Pulsa' },
  { id: 'voucher', name: 'Voucher' },
];

const TELEMETRY_LOGS = [
  '[SYS_OK] Transmitted 500 Diamond → MLBB orbit-44',
  '[LINK]   DigiFlazz gateway handshake established',
  '[TX_OK]  86 UC delivered → FreeFire sector-7',
  '[SYS_OK] Voucher payload 50K dispatched',
  '[PING]   Payment gateway latency: 12ms ✓',
  '[TX_OK]  Valorant 1650 VP → ID 81920843',
  '[SYS_OK] Call of Duty 400 CP orbit confirmed',
  '[LINK]   Reseller node synced — tier COMMANDER',
  '[TX_OK]  Genshin Impact 980 Genesis → UID 712',
  '[SYS_OK] QRIS token generated — TXN 0x9AF2',
  '[PING]   SSL certificate valid · chain intact',
  '[TX_OK]  Pulsa 25K → 08123456789 transmitted',
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [digiflazzBrands, setDigiflazzBrands] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [products, digiflazzBrands, activeCategory, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live clock tick for HUD
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const fetchAll = async () => {
    try {
      const [seedRes, catalogRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/products`),
        axios.get(`${API_URL}/api/biller/catalog`),
      ]);

      if (seedRes.status === 'fulfilled') {
        setProducts(seedRes.value.data.products || []);
      }

      if (catalogRes.status === 'fulfilled' && catalogRes.value.data.success) {
        setDigiflazzBrands(catalogRes.value.data.brands || []);
      }

      try { await axios.post(`${API_URL}/api/seed`); } catch {}
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    const allItems = [];

    for (const brand of digiflazzBrands) {
      allItems.push({
        id: `df-${brand.slug}`,
        slug: `df/${brand.slug}`,
        name: brand.brand,
        image: brand.image,
        category: brand.category,
        source: 'digiflazz',
        itemCount: brand.items.length,
        startPrice: Math.min(...brand.items.map(i => i.price)),
      });
    }

    const dfNames = new Set(digiflazzBrands.map(b => b.brand.toLowerCase()));
    for (const p of products) {
      if (!dfNames.has(p.name.toLowerCase())) {
        const catMap = { game: 'games', voucher: 'voucher' };
        allItems.push({ ...p, category: catMap[p.category] || p.category, source: 'seed' });
      }
    }

    let filtered = [...allItems];

    if (activeCategory !== 'all') {
      filtered = filtered.filter(p => p.category === activeCategory);
    }

    if (searchQuery) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredProducts(filtered);
  };

  const now = new Date();
  const timeStr = now.toTimeString().split(' ')[0];
  const dateStr = now.toISOString().split('T')[0];

  return (
    <div className="min-h-screen pt-16">
      {/* ── HERO: 3-Column Cybernetic Dashboard ── */}
      <section className="relative overflow-hidden cyber-scanline">
        {/* Background */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${HERO_BG}')` }}
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="cyber-grid absolute inset-0 opacity-60" />

        {/* Ambient glows */}
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px] -translate-x-1/3 -translate-y-1/3" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-[100px] translate-x-1/4 translate-y-1/4" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">

            {/* ── COL 1: Telemetry Console ── */}
            <div className="hidden lg:flex lg:col-span-3 flex-col">
              <div className="cyber-card rounded-xl bg-card/60 backdrop-blur-sm h-full overflow-hidden">
                {/* Header */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-black/30">
                  <Activity className="w-4 h-4 text-primary animate-pulse" />
                  <span className="font-mono text-xs text-primary tracking-widest uppercase">SYS_TELEMETRY</span>
                  <span className="ml-auto font-mono text-[10px] text-muted-foreground animate-flicker">{timeStr}</span>
                </div>
                {/* Scrolling log */}
                <div className="relative h-64 overflow-hidden">
                  <div className="animate-data-stream">
                    {[...TELEMETRY_LOGS, ...TELEMETRY_LOGS].map((log, i) => (
                      <div key={i} className="px-4 py-1.5 font-mono text-[10px] leading-relaxed border-b border-border/30">
                        <span className={
                          log.startsWith('[TX_OK]') ? 'text-primary' :
                          log.startsWith('[SYS_OK]') ? 'text-emerald-400' :
                          log.startsWith('[LINK]') ? 'text-secondary' :
                          'text-muted-foreground'
                        }>
                          {log}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Status bar */}
                <div className="px-4 py-3 border-t border-border bg-black/20">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                    <span className="font-mono text-[10px] text-primary">ALL_SYSTEMS_NOMINAL</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: 'UPTIME', val: '99.98%' },
                      { label: 'TX/MIN', val: `${42 + (tick % 18)}` },
                      { label: 'LATENCY', val: '11ms' },
                      { label: 'NODES', val: '12/12' },
                    ].map(s => (
                      <div key={s.label} className="bg-black/30 rounded px-2 py-1">
                        <p className="font-mono text-[9px] text-muted-foreground">{s.label}</p>
                        <p className="font-mono text-xs text-primary font-bold">{s.val}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* ── COL 2: Central Command Hub ── */}
            <div className="lg:col-span-6 flex flex-col justify-center">
              {/* HUD Date badge */}
              <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/5 w-fit">
                <Radio className="w-3 h-3 text-primary animate-pulse" />
                <span className="font-mono text-xs text-primary tracking-widest">SYS_ACTIVE · {dateStr}</span>
              </div>

              {/* Title */}
              <h1 className="font-bold text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight mb-2 leading-tight">
                {COPY.hero.titleLine1}
              </h1>
              <h2 className="font-bold text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight mb-6 leading-tight brand-gradient text-glow-green">
                {COPY.hero.titleLine2}
              </h2>

              <p className="text-foreground/70 text-sm md:text-base mb-8 max-w-xl leading-relaxed">
                {COPY.hero.subtitle}
              </p>

              {/* Search — HUD bracket style */}
              <div className="relative hud-bracket max-w-lg">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-primary/70" />
                <Input
                  type="text"
                  placeholder={COPY.hero.searchPlaceholder}
                  className="pl-11 pr-4 py-6 bg-black/60 border-primary/30 text-white placeholder:text-muted-foreground rounded-lg focus:border-primary focus:ring-1 focus:ring-primary/40 font-mono text-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-input"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-mono text-primary text-lg leading-none animate-blink">_</span>
              </div>

              {/* Quick stats */}
              <div className="flex items-center gap-6 mt-6">
                {[
                  { label: 'Products', val: loading ? '…' : filteredProducts.length.toString() },
                  { label: 'Instant', val: '< 3s' },
                  { label: 'Uptime', val: '99.9%' },
                ].map(s => (
                  <div key={s.label}>
                    <p className="font-mono text-xl font-bold text-primary text-glow-green">{s.val}</p>
                    <p className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* ── COL 3: Orbital Visualizer ── */}
            <div className="hidden lg:flex lg:col-span-3 flex-col items-center justify-center">
              <div className="cyber-card rounded-xl bg-card/60 backdrop-blur-sm p-4 w-full">
                {/* Header */}
                <div className="flex items-center gap-2 mb-4">
                  <Cpu className="w-4 h-4 text-secondary" />
                  <span className="font-mono text-[10px] text-secondary tracking-widest uppercase">CORE_VISUALIZER</span>
                </div>
                <div className="flex justify-center">
                  <HeroVisual />
                </div>
                <div className="mt-4 grid grid-cols-3 gap-1">
                  {['FLUX', 'ORB', 'SYNC'].map((label, i) => (
                    <div key={label} className="text-center py-1 rounded bg-black/40 border border-border">
                      <div className={`w-1.5 h-1.5 rounded-full mx-auto mb-1 ${i === 0 ? 'bg-primary animate-pulse' : i === 1 ? 'bg-secondary animate-pulse' : 'bg-accent'}`} />
                      <p className="font-mono text-[9px] text-muted-foreground">{label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── FEATURES BAR ── */}
      <section className="py-10 border-b border-border bg-card/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {(() => {
              const featureIcons = [Zap, Tag, Shield, Headphones];
              const tones = [
                'border-primary/30 bg-primary/10 text-primary',
                'border-secondary/30 bg-secondary/10 text-secondary',
                'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
                'border-accent/30 bg-accent/10 text-accent',
              ];
              return COPY.features.map((f, idx) => {
                const Icon = featureIcons[idx % featureIcons.length];
                const tone = tones[idx % tones.length];
                return (
                  <div key={idx} className="flex items-center gap-3 group">
                    <div className={`w-12 h-12 rounded-xl border flex items-center justify-center flex-shrink-0 transition-all group-hover:scale-110 ${tone}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-sm">{f.label}</h3>
                      <p className="text-xs text-muted-foreground">{f.desc}</p>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </section>

      {/* ── PRODUCTS GRID ── */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Tabs — cockpit-style buttons */}
          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                className={`px-5 py-2 rounded-lg font-mono text-sm uppercase tracking-widest border transition-all duration-200 ${
                  activeCategory === cat.id
                    ? 'bg-primary/20 border-primary text-primary neon-green'
                    : 'bg-transparent border-border text-muted-foreground hover:border-primary/40 hover:text-primary/80'
                }`}
                onClick={() => setActiveCategory(cat.id)}
                data-testid={`category-${cat.id}`}
              >
                {cat.name}
              </button>
            ))}

            {!loading && (
              <span className="ml-auto font-mono text-xs text-muted-foreground">
                {filteredProducts.length} UNIT{filteredProducts.length !== 1 ? 'S' : ''} FOUND
              </span>
            )}
          </div>

          {/* Grid */}
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
              {[...Array(10)].map((_, i) => <GameCardSkeleton key={i} />)}
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
              {filteredProducts.map((product) => (
                <GameCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="font-mono text-muted-foreground">[SCAN_EMPTY] — No units found in this sector</p>
            </div>
          )}
        </div>
      </section>

      {/* ── CTA SECTION ── */}
      <section className="py-16 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-card via-card to-card border border-border cyber-card p-8 md:p-12">
            {/* Glows */}
            <div className="absolute top-0 right-0 w-80 h-80 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
            <div className="absolute bottom-0 left-0 w-60 h-60 bg-secondary/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" />
            {/* Cyber grid overlay */}
            <div className="absolute inset-0 cyber-grid opacity-30 rounded-2xl" />

            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="max-w-xl">
                <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full border border-secondary/30 bg-secondary/5">
                  <Radio className="w-3 h-3 text-secondary" />
                  <span className="font-mono text-[10px] text-secondary tracking-widest uppercase">ALLIANCE_PROGRAM</span>
                </div>
                <h2 className="font-bold text-2xl md:text-3xl text-white uppercase mb-4 leading-tight">
                  {COPY.cta.titlePrefix}<span className="text-glow-green brand-gradient">{COPY.cta.titleHighlight}</span>{COPY.cta.titleSuffix}
                </h2>
                <p className="text-foreground/70 mb-6 text-sm leading-relaxed">
                  {COPY.cta.subtitle}
                </p>
                <Button
                  className="bg-primary hover:bg-primary/80 text-black font-bold uppercase tracking-widest py-6 px-8 neon-green transition-all duration-300"
                  onClick={() => window.location.href = '/reseller'}
                  data-testid="cta-reseller-btn"
                >
                  {COPY.cta.button}
                </Button>
              </div>

              {/* Compact visualizer */}
              <div className="hidden md:block flex-shrink-0">
                <HeroVisual compact />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
